import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/EmailChangeModal.css";

const EmailChangeModal = ({ isOpen, onClose, currentEmail, userName }) => {
  const [step, setStep] = useState(1); // 1: Request, 2: Verify
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  const getApiBaseUrl = () => {
    const url = process.env.REACT_APP_API_BASE_URL;
    if (!url || !url.startsWith("http")) {
      // Surface a clear error rather than falling back to a guessed host. The previous
      // fallback pointed at https://ogestyle.azurewebsites.net, which does not exist, so a
      // missing REACT_APP_API_BASE_URL produced a request to a dead host instead of a
      // diagnosable failure. Matches the canonical helper in utils/apiHelpers.js.
      throw new Error("API configuration error");
    }
    return url;
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  };

  const handleRequestChange = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(
        `${getApiBaseUrl()}/api/email-change/request-change`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ newEmail, password }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to request email change");
      }

      setSuccess(
        `Verification email sent to ${newEmail}. Please check your inbox and enter the verification code.`,
      );
      setStep(2);
      setPassword(""); // Clear password for security
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyChange = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(
        `${getApiBaseUrl()}/api/email-change/verify-change`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ token: verificationToken }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to verify email change");
      }

      setSuccess(
        "Email changed successfully! Please log in with your new email.",
      );

      // Clear token and redirect to login after 2 seconds
      setTimeout(() => {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(
        `${getApiBaseUrl()}/api/email-change/cancel-request`,
        {
          method: "DELETE",
          headers,
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to cancel request");
      }

      setSuccess("Email change request cancelled");
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setNewEmail("");
    setPassword("");
    setVerificationToken("");
    setError("");
    setSuccess("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="email-change-modal-overlay" onClick={handleClose}>
      <div
        className="email-change-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="email-change-modal-close" onClick={handleClose}>
          ×
        </button>

        {step === 1 && (
          <>
            <h2>Change Your Email Address</h2>
            <p className="email-change-current-email">
              Current email: <strong>{currentEmail}</strong>
            </p>

            <form onSubmit={handleRequestChange}>
              <div className="email-change-form-group">
                <label htmlFor="newEmail">New Email Address</label>
                <input
                  type="email"
                  id="newEmail"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                  placeholder="Enter your new email"
                  disabled={loading}
                />
              </div>

              <div className="email-change-form-group">
                <label htmlFor="password">Current Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Confirm your password"
                  disabled={loading}
                />
              </div>

              {error && <div className="email-change-error">{error}</div>}
              {success && <div className="email-change-success">{success}</div>}

              <div className="email-change-form-actions">
                <button
                  type="button"
                  className="email-change-btn-cancel"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="email-change-btn-submit"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send Verification Email"}
                </button>
              </div>
            </form>

            <div className="email-change-info-box">
              <h4>🔒 Security Information:</h4>
              <ul>
                <li>We'll send a verification email to your new address</li>
                <li>The verification link expires in 24 hours</li>
                <li>
                  You'll need to log in with your new email after verification
                </li>
                <li>Your password will remain the same</li>
              </ul>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2>Verify Your New Email</h2>
            <p className="email-change-verify-message">
              We sent a verification email to <strong>{newEmail}</strong>.
              Please check your inbox and enter the verification code below.
            </p>

            <form onSubmit={handleVerifyChange}>
              <div className="email-change-form-group">
                <label htmlFor="verificationToken">Verification Code</label>
                <input
                  type="text"
                  id="verificationToken"
                  value={verificationToken}
                  onChange={(e) => setVerificationToken(e.target.value)}
                  required
                  placeholder="Enter verification code from email"
                  disabled={loading}
                />
              </div>

              {error && <div className="email-change-error">{error}</div>}
              {success && <div className="email-change-success">{success}</div>}

              <div className="email-change-form-actions">
                <button
                  type="button"
                  className="email-change-btn-cancel"
                  onClick={handleCancelRequest}
                  disabled={loading}
                >
                  Cancel Request
                </button>
                <button
                  type="submit"
                  className="email-change-btn-submit"
                  disabled={loading}
                >
                  {loading ? "Verifying..." : "Verify & Change Email"}
                </button>
              </div>
            </form>

            <div className="email-change-info-box">
              <h4>📧 Didn't receive the email?</h4>
              <ul>
                <li>Check your spam/junk folder</li>
                <li>Wait a few minutes for delivery</li>
                <li>Verify you entered the correct email address</li>
                <li>Click "Cancel Request" to start over</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailChangeModal;
